import { lazy, Suspense } from "react";
import { CalendarDays, type LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { motionTokens, springs } from "@/shared/lib/motion";

// Lecteur Remotion chargé à la demande : il n'alourdit pas le premier affichage.
const BannerPlayer = lazy(() => import("@/shared/remotion/BannerPlayer"));

type DashboardPageHeaderProps = {
  title: string;
  subtitle?: string;
  /** Conservé pour compatibilité : le bandeau affiche l'illustration à la place de l'icône. */
  icon?: LucideIcon;
  /** Nom de l'organisation : affiché en grand, l'intitulé passe alors en surtitre. */
  badge?: string;
  date?: string;
};

/**
 * Bandeau d'en-tête des tableaux de bord : surtitre, organisation, périmètre,
 * illustration animée et date (même design que Safecheck-assureur).
 */
export function DashboardPageHeader({ title, subtitle, badge, date }: DashboardPageHeaderProps) {
  const heading = badge || title;
  const eyebrow = badge ? title : null;

  return (
    <section className="flex min-h-[8.5rem] flex-wrap items-center gap-4 overflow-hidden rounded-[var(--radius-card)] border border-border bg-[linear-gradient(90deg,#f7fbf5_0%,#eef8e7_45%,#cdf0b0_100%)] px-6 shadow-[var(--shadow-card)] dark:bg-[linear-gradient(90deg,#0f2125_0%,#10271f_55%,#1d3d17_100%)] lg:flex-nowrap">
      <motion.div
        className="min-w-0 shrink-0 py-6"
        initial={{ opacity: 0, x: -motionTokens.distance.md }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: motionTokens.duration.slow, ease: motionTokens.easing.smooth }}
      >
        {eyebrow ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{eyebrow}</p>
        ) : null}
        <h1 className="mt-1 text-2xl font-bold uppercase tracking-tight text-foreground sm:text-[1.75rem]">{heading}</h1>
        {subtitle ? <p className="mt-1 text-sm text-muted-foreground text-pretty">{subtitle}</p> : null}
      </motion.div>

      {/* Illustration animée (Remotion) entre le texte et la date : elle monte à l'arrivée,
          se rétrécit puis disparaît faute de place. */}
      <motion.div
        className="pointer-events-none hidden h-[8.5rem] min-w-0 flex-1 self-end overflow-hidden lg:block [mask-image:linear-gradient(90deg,transparent,black_25%)]"
        initial={{ opacity: 0, y: motionTokens.distance.md * 2 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springs.gentle, delay: motionTokens.stagger * 2 }}
        aria-hidden="true"
      >
        <Suspense fallback={null}>
          <BannerPlayer />
        </Suspense>
      </motion.div>

      {date ? (
        <div className="mb-6 ml-auto flex shrink-0 items-center lg:mb-0">
          <span className="inline-flex h-11 items-center gap-3 rounded-xl border border-border bg-card pl-1.5 pr-4 text-sm font-semibold tabular-nums text-foreground shadow-[var(--shadow-sm)]">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent text-accent-foreground" aria-hidden="true">
              <CalendarDays size={16} strokeWidth={2} />
            </span>
            {date}
          </span>
        </div>
      ) : null}
    </section>
  );
}
